'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { X, MapPin, Clock, Phone, User, ExternalLink, AlertTriangle, CheckCircle, Package, Calendar, Heart, RefreshCw, Copy, PhoneCall, MessageCircle, BookOpen } from 'lucide-react';
import type { Pantry, PantryStatus, AvailabilityLevel } from '@/lib/pantryData';
import { updatePantry } from '@/lib/pantryData';
import { upsertPantryStatus } from '@/lib/pantryStatusService';
import Link from 'next/link';

interface PantryDetailPanelProps {
  pantry: Pantry;
  onClose: () => void;
  onReport: (pantry: Pantry) => void;
  onDonate?: () => void;
  isMobile?: boolean;
  onStatusUpdate?: (updated: Pantry) => void;
  allPantries?: Pantry[];
  onSelectPantry?: (pantry: Pantry) => void;
}

const STATUS_CONFIG = {
  'aktibo': {
    label: 'Aktibo',
    className: 'badge-aktibo',
    icon: CheckCircle,
    iconColor: 'text-[#16a34a]',
    bg: 'bg-[#f0fdf4]',
    border: 'border-[#bbf7d0]',
  },
  'mababa-stock': {
    label: 'Mababa ang Stock',
    className: 'badge-mababa',
    icon: AlertTriangle,
    iconColor: 'text-[#ca8a04]',
    bg: 'bg-[#fefce8]',
    border: 'border-[#fde68a]',
  },
  'walang-stock': {
    label: 'Walang Stock',
    className: 'badge-walang-stock',
    icon: Package,
    iconColor: 'text-[#dc2626]',
    bg: 'bg-[#fff1f2]',
    border: 'border-[#fecdd3]',
  },
  'hindi-aktibo': {
    label: 'Sarado',
    className: 'bg-[#f3f4f6] text-[#6b7280]',
    icon: X,
    iconColor: 'text-[#9ca3af]',
    bg: 'bg-[#f9fafb]',
    border: 'border-[#e5e7eb]',
  },
};

const STATUS_OPTIONS: { value: PantryStatus; label: string; color: string }[] = [
  { value: 'aktibo', label: 'Aktibo (Active)', color: 'text-[#16a34a]' },
  { value: 'mababa-stock', label: 'Mababa ang Stock (Low in Stock)', color: 'text-[#ca8a04]' },
  { value: 'walang-stock', label: 'Walang Stock (No Stock)', color: 'text-[#dc2626]' },
  { value: 'hindi-aktibo', label: 'Sarado (Closed)', color: 'text-[#6b7280]' },
];

const AVAILABILITY_OPTIONS: { value: AvailabilityLevel; label: string; color: string; bg: string }[] = [
  { value: 'mataas', label: 'Mataas', color: 'text-[#16a34a]', bg: 'bg-[#dcfce7]' },
  { value: 'katamtaman', label: 'Katamtaman', color: 'text-[#ca8a04]', bg: 'bg-[#fef9c3]' },
  { value: 'mababa', label: 'Mababa', color: 'text-[#ea580c]', bg: 'bg-[#ffedd5]' },
  { value: 'wala', label: 'Wala', color: 'text-[#dc2626]', bg: 'bg-[#fee2e2]' },
];

type UpdateStep = 'idle' | 'password' | 'select' | 'success';

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('fil-PH', {
    month: 'long', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function getDistanceKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function buildSocialUrl(raw: string, platform: 'facebook' | 'instagram' | 'twitter' | 'messenger'): string {
  const trimmed = raw.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  const bases: Record<string, string> = {
    facebook: 'https://facebook.com/',
    instagram: 'https://instagram.com/',
    twitter: 'https://x.com/',
    messenger: 'https://m.me/',
  };
  return bases[platform] + trimmed.replace(/^@/, '');
}

// Social icons as inline SVGs
const FacebookIcon = ({ size = 13 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const InstagramIcon = ({ size = 13 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const XTwitterIcon = ({ size = 13 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

export default function PantryDetailPanel({
  pantry,
  onClose,
  onReport,
  onDonate,
  isMobile = false,
  onStatusUpdate,
  allPantries = [],
  onSelectPantry,
}: PantryDetailPanelProps) {
  const config = STATUS_CONFIG[pantry.status];
  const StatusIcon = config.icon;

  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${pantry.lat},${pantry.lng}`;
  const profileUrl = `/pantry/${pantry.id}`;
  const manageUrl = `/pantry-owner/login`;

  // Update Status flow — kept for legacy inline use if needed
  const [updateStep, setUpdateStep] = useState<UpdateStep>('idle');
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<PantryStatus>(pantry.status);
  const [itemAvailability, setItemAvailability] = useState<Record<string, AvailabilityLevel>>(
    pantry.itemAvailability ?? {}
  );
  const [editItems, setEditItems] = useState<string[]>(pantry.items);
  const [newItemInput, setNewItemInput] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Nearby pantries (within 5km, excluding self)
  const nearbyPantries = allPantries
    .filter((p) => p.id !== pantry.id)
    .map((p) => ({ ...p, distance: getDistanceKm(pantry.lat, pantry.lng, p.lat, p.lng) }))
    .filter((p) => p.distance <= 5)
    .sort((a, b) => a.distance - b.distance)
    .slice(0, 5);

  const handlePasswordSubmit = () => {
    const correctPassword = pantry.pantryPassword || 'B@yan1han2026';
    if (passwordInput === correctPassword) {
      setPasswordError('');
      setSelectedStatus(pantry.status);
      setItemAvailability(pantry.itemAvailability ?? {});
      setEditItems(pantry.items);
      setUpdateStep('select');
    } else {
      setPasswordError('Mali ang password. Subukan muli.');
    }
  };

  const handleStatusUpdate = () => {
    setIsUpdating(true);
    const updated: Pantry = {
      ...pantry,
      status: selectedStatus,
      hulingUpdate: new Date().toISOString(),
      itemAvailability,
      items: editItems,
    };
    updatePantry(updated);
    onStatusUpdate?.(updated);
    upsertPantryStatus(updated.id, updated.status, updated.hulingUpdate, itemAvailability).finally(() => {
      setIsUpdating(false);
      setUpdateStep('success');
      setTimeout(() => setUpdateStep('idle'), 2500);
    });
  };

  const handleCancelUpdate = () => {
    setUpdateStep('idle');
    setPasswordInput('');
    setPasswordError('');
  };

  const setItemLevel = (item: string, level: AvailabilityLevel) => {
    setItemAvailability((prev) => ({ ...prev, [item]: level }));
  };

  const addNewItem = () => {
    const trimmed = newItemInput.trim();
    if (!trimmed || editItems.includes(trimmed)) return;
    setEditItems((prev) => [...prev, trimmed]);
    setNewItemInput('');
  };

  const removeItem = (item: string) => {
    setEditItems((prev) => prev.filter((i) => i !== item));
    setItemAvailability((prev) => {
      const next = { ...prev };
      delete next[item];
      return next;
    });
  };

  const handleCopyPhone = useCallback(() => {
    if (!pantry.contact) return;
    navigator.clipboard.writeText(pantry.contact).then(() => {
      setCopiedPhone(true);
      setTimeout(() => setCopiedPhone(false), 2000);
    });
  }, [pantry.contact]);

  const hasSocial = pantry.facebookUrl || pantry.instagramUrl || pantry.twitterUrl || pantry.messengerUrl;

  return (
    <div className={`flex flex-col ${isMobile ? 'h-full' : 'h-full'}`}>
      {/* Mobile drag handle */}
      {isMobile && (
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1.5 bg-[#d1ead9] rounded-full" />
        </div>
      )}

      {isMobile ? (
        /* ── MOBILE COMPACT LAYOUT ── */
        <div className="flex flex-col overflow-hidden" style={{ maxHeight: 'calc(52vh - 20px)' }}>
          {/* ── INFO SECTION (scrollable, white bg) ── */}
          <div className="flex-1 overflow-y-auto bg-white">
            {/* Compact header: name + status + close */}
            <div className="px-5 pt-2 pb-3 flex-shrink-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h2 className="font-display text-lg font-bold text-[#1e5c38] leading-tight">
                    {pantry.pangalan}
                  </h2>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${config.className}`}>
                      <StatusIcon size={11} className={config.iconColor} />
                      {config.label}
                    </span>
                    <span className="text-xs text-[#6b7280]">•</span>
                    <span className="text-xs text-[#6b7280]">{pantry.lungsod}</span>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-full bg-[#f3f4f6] text-[#6b7280] hover:bg-[#e5e7eb] hover:text-[#374151] transition-colors flex-shrink-0 mt-0.5"
                  aria-label="Isara"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Address */}
              <div className="flex items-start gap-1.5 mt-2.5">
                <MapPin size={14} className="text-[#2d7a4f] mt-0.5 flex-shrink-0" />
                <span className="text-sm text-[#374151]">{pantry.address}, {pantry.barangay}, {pantry.lungsod}</span>
              </div>

              {/* Operating Hours */}
              <div className="flex items-center gap-1.5 mt-2">
                <Clock size={14} className="text-[#2d7a4f] flex-shrink-0" />
                <span className="text-sm text-[#374151]">{pantry.oras}</span>
              </div>

              {/* Item tags */}
              {pantry.items.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {pantry.items.slice(0, 4).map((item) => (
                    <span
                      key={`mobile-item-${pantry.id}-${item}`}
                      className="px-2.5 py-1 text-xs font-medium rounded-full bg-[#e8f5ee] text-[#1e5c38] border border-[#bbf7d0]"
                    >
                      {item}
                    </span>
                  ))}
                  {pantry.items.length > 4 && (
                    <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-[#f3f4f6] text-[#6b7280] border border-[#e5e7eb]">
                      +{pantry.items.length - 4} pa
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Scroll hint label */}
            <div className="px-5 pb-2">
              <p className="text-xs text-[#9ca3af] italic">↕ i-scroll para sa higit pang detalye</p>
            </div>

            {/* Scrollable extra details */}
            <div className="px-5 pb-4 space-y-4 border-t border-[#e8f5ee]">
              {/* Tungkol sa Pantry section */}
              {(pantry.photoUrl || pantry.description) && (
                <div className="pt-3">
                  <p className="text-xs font-semibold text-[#9ca3af] uppercase tracking-wider mb-3">Tungkol sa Pantry</p>
                  {pantry.photoUrl && (
                    <div className="rounded-xl overflow-hidden border border-[#d1ead9] h-36 mb-3">
                      <img src={pantry.photoUrl} alt={`Larawan ng ${pantry.pangalan}`} className="w-full h-full object-cover" />
                    </div>
                  )}
                  {pantry.description && (
                    <p className="text-sm text-[#374151] leading-relaxed">{pantry.description}</p>
                  )}
                </div>
              )}

              {/* Alamin Pa button */}
              <div>
                <a
                  href={profileUrl}
                  className="flex items-center justify-center gap-2 w-full bg-[#f0faf4] border border-[#d1ead9] text-[#2d7a4f] hover:bg-[#e8f5ee] active:scale-95 font-semibold text-sm py-2.5 rounded-xl transition-all duration-150"
                >
                  <BookOpen size={15} />
                  Profile ng Pantry
                </a>
              </div>

              {/* All items with availability */}
              {pantry.items.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-[#9ca3af] uppercase tracking-wider mb-2">Mga Available na Pagkain</p>
                  <div className="flex flex-wrap gap-2">
                    {pantry.items.map((item) => {
                      const level = pantry.itemAvailability?.[item];
                      const avail = level ? AVAILABILITY_OPTIONS.find((a) => a.value === level) : null;
                      return (
                        <span
                          key={`item-full-${pantry.id}-${item}`}
                          className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border ${
                            avail
                              ? `${avail.bg} ${avail.color} border-transparent`
                              : 'bg-[#e8f5ee] text-[#1e5c38] border-[#bbf7d0]'
                          }`}
                        >
                          {item}
                          {avail && <span className="opacity-75">· {avail.label}</span>}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Contact */}
              <div>
                <p className="text-xs font-semibold text-[#9ca3af] uppercase tracking-wider mb-2">Makipag-ugnayan</p>
                {pantry.contactPerson && (
                  <div className="flex items-center gap-2 text-sm text-[#374151] mb-2">
                    <User size={15} className="text-[#2d7a4f] flex-shrink-0" />
                    <span className="font-medium">{pantry.contactPerson}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Phone size={15} className="text-[#2d7a4f] flex-shrink-0" />
                  <span className="text-sm text-[#374151] flex-1">{pantry.contact}</span>
                  <button
                    onClick={handleCopyPhone}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border transition-all ${
                      copiedPhone
                        ? 'bg-[#dcfce7] text-[#16a34a] border-[#bbf7d0]'
                        : 'bg-white text-[#6b7280] border-[#e5e7eb] hover:border-[#d1ead9]'
                    }`}
                  >
                    <Copy size={12} />
                    {copiedPhone ? 'Nakopya!' : 'Kopyahin'}
                  </button>
                  <a
                    href={`tel:${pantry.contact.replace(/\s/g, '')}`}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border bg-[#e8f5ee] text-[#2d7a4f] border-[#d1ead9] hover:bg-[#d1ead9] transition-all"
                  >
                    <PhoneCall size={12} />
                    Tawagan
                  </a>
                </div>
                {hasSocial && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {pantry.facebookUrl && (
                      <a href={buildSocialUrl(pantry.facebookUrl, 'facebook')} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#1877f2]/10 text-[#1877f2] hover:bg-[#1877f2]/20 transition-colors border border-[#1877f2]/20">
                        <FacebookIcon size={13} /> Facebook
                      </a>
                    )}
                    {pantry.instagramUrl && (
                      <a href={buildSocialUrl(pantry.instagramUrl, 'instagram')} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#e1306c]/10 text-[#e1306c] hover:bg-[#e1306c]/20 transition-colors border border-[#e1306c]/20">
                        <InstagramIcon size={13} /> Instagram
                      </a>
                    )}
                    {pantry.twitterUrl && (
                      <a href={buildSocialUrl(pantry.twitterUrl, 'twitter')} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-black/10 text-black hover:bg-black/20 transition-colors border border-black/10">
                        <XTwitterIcon size={13} /> X / Twitter
                      </a>
                    )}
                    {pantry.messengerUrl && (
                      <a href={buildSocialUrl(pantry.messengerUrl, 'messenger')} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#0084ff]/10 text-[#0084ff] hover:bg-[#0084ff]/20 transition-colors border border-[#0084ff]/20">
                        <MessageCircle size={13} /> Messenger
                      </a>
                    )}
                  </div>
                )}
              </div>

              {/* Organizer */}
              <div>
                <p className="text-xs font-semibold text-[#9ca3af] uppercase tracking-wider mb-2">Organizer</p>
                <div className="flex items-center gap-2 text-sm text-[#374151]">
                  <User size={15} className="text-[#2d7a4f] flex-shrink-0" />
                  <span>{pantry.nagtatag}</span>
                </div>
              </div>

              {/* Last Updated */}
              <div className="flex items-center gap-2 text-xs text-[#9ca3af] pt-1 border-t border-[#e8f5ee]">
                <Calendar size={13} />
                <span>Huling na-update: {formatDate(pantry.hulingUpdate)}</span>
              </div>

              {/* Nearby Pantries */}
              {nearbyPantries.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-[#9ca3af] uppercase tracking-wider mb-2">Pantry na Malapit</p>
                  <div className="space-y-2">
                    {nearbyPantries.map((np) => {
                      const npConfig = STATUS_CONFIG[np.status];
                      const NpIcon = npConfig.icon;
                      return (
                        <button
                          key={`nearby-mobile-${np.id}`}
                          onClick={() => onSelectPantry?.(np)}
                          className="w-full flex items-center gap-3 p-2.5 bg-[#f8fdf9] border border-[#d1ead9] rounded-xl text-left hover:bg-[#edf7f1] hover:border-[#a7d9b8] transition-colors"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-[#1e5c38] truncate">{np.pangalan}</p>
                            <p className="text-xs text-[#6b7280] truncate">{np.barangay}, {np.lungsod}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-semibold ${npConfig.className}`}>
                              <NpIcon size={9} className={npConfig.iconColor} />
                              {npConfig.label}
                            </span>
                            <span className="text-xs font-semibold text-[#2d7a4f] bg-[#e8f5ee] px-2 py-0.5 rounded-full">
                              {np.distance < 1 ? `${Math.round(np.distance * 1000)}m` : `${np.distance.toFixed(1)}km`}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── ACTION BUTTONS SECTION (always visible, distinct bg) ── */}
          <div className="flex-shrink-0 bg-[#f0faf4] border-t-2 border-[#d1ead9] px-4 py-3 space-y-2">
            {/* Primary: Puntahan */}
            <a
              href={directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full bg-[#2d7a4f] hover:bg-[#1e5c38] active:scale-95 text-white font-semibold text-sm py-3 rounded-xl transition-all duration-150"
            >
              <ExternalLink size={16} />
              Puntahan
            </a>

            {/* Secondary: Donate */}
            {onDonate && (
              <button
                onClick={onDonate}
                className="flex items-center justify-center gap-2 w-full bg-[#fff7ed] border border-[#fed7aa] text-[#c2410c] hover:bg-[#ffedd5] active:scale-95 font-semibold text-sm py-2.5 rounded-xl transition-all duration-150"
              >
                <Heart size={15} />
                Mag-donate sa Pantry na Ito
              </button>
            )}

            {/* Tertiary row: Feedback + Manage */}
            <div className="flex gap-2">
              <button
                onClick={() => onReport(pantry)}
                className="flex items-center justify-center gap-1.5 flex-1 border border-[#d1ead9] bg-white text-[#6b7280] hover:bg-[#f9fafb] active:scale-95 font-medium text-xs py-2.5 rounded-xl transition-all duration-150"
              >
                <MessageCircle size={14} />
                Magbigay ng Feedback
              </button>
              <Link
                href={manageUrl}
                className="flex items-center justify-center gap-1.5 flex-1 border border-[#d1ead9] bg-white text-[#2d7a4f] hover:bg-[#f0faf4] active:scale-95 font-medium text-xs py-2.5 rounded-xl transition-all duration-150"
              >
                <RefreshCw size={14} />
                Manage
              </Link>
            </div>
          </div>
        </div>
      ) : (
        /* ── DESKTOP LAYOUT ── */
        <>
          {/* Header */}
          <div className={`px-5 py-4 border-b border-[#e8f5ee] ${config.bg} flex-shrink-0`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <a href={profileUrl} className="hover:underline">
                  <h2 className="font-display text-lg font-700 text-[#1e5c38] leading-tight mb-1 truncate">
                    {pantry.pangalan}
                  </h2>
                </a>
                <div className="flex items-center gap-1.5 text-sm text-[#4b5563]">
                  <MapPin size={13} className="text-[#2d7a4f] flex-shrink-0" />
                  <span className="truncate">{pantry.lungsod}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${config.className}`}>
                  <StatusIcon size={12} className={config.iconColor} />
                  {config.label}
                </span>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg text-[#6b7280] hover:bg-white/80 hover:text-[#374151] transition-colors"
                  aria-label="Isara"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
            {/* Address */}
            <div>
              <p className="text-xs font-semibold text-[#9ca3af] uppercase tracking-wider mb-2">Address</p>
              <div className="flex items-start gap-2 text-sm text-[#374151]">
                <MapPin size={15} className="text-[#2d7a4f] mt-0.5 flex-shrink-0" />
                <span>{pantry.address}, {pantry.barangay}, {pantry.lungsod}</span>
              </div>
              {pantry.nearestLandmark && (
                <div className="flex items-start gap-2 text-xs text-[#6b7280] mt-1.5 ml-5">
                  <span className="text-[#2d7a4f]">📍</span>
                  <span>Malapit sa: {pantry.nearestLandmark}</span>
                </div>
              )}
            </div>
            {/* Oras ng Operasyon */}
            <div>
              <p className="text-xs font-semibold text-[#9ca3af] uppercase tracking-wider mb-2">Oras ng Operasyon</p>
              <div className="flex items-center gap-2 text-sm text-[#374151]">
                <Clock size={15} className="text-[#2d7a4f] flex-shrink-0" />
                <span>{pantry.oras}</span>
              </div>
            </div>
            {/* Mga Available na Pagkain */}
            <div>
              <p className="text-xs font-semibold text-[#9ca3af] uppercase tracking-wider mb-2">Mga Available na Pagkain</p>
              {pantry.items.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {pantry.items.map((item) => {
                    const level = pantry.itemAvailability?.[item];
                    const avail = level ? AVAILABILITY_OPTIONS.find((a) => a.value === level) : null;
                    return (
                      <span
                        key={`item-${pantry.id}-${item}`}
                        className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border ${
                          avail
                            ? `${avail.bg} ${avail.color} border-transparent`
                            : 'bg-[#e8f5ee] text-[#1e5c38] border-[#bbf7d0]'
                        }`}
                      >
                        {item}
                        {avail && <span className="opacity-75">· {avail.label}</span>}
                      </span>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center gap-2 py-3 px-3 bg-[#fff1f2] rounded-lg border border-[#fecdd3]">
                  <Package size={16} className="text-[#dc2626]" />
                  <p className="text-sm text-[#991b1b]">Wala pang pagkain ngayon.</p>
                </div>
              )}
            </div>
            {/* Tungkol sa Pantry */}
            {(pantry.photoUrl || pantry.description) && (
              <div>
                <p className="text-xs font-semibold text-[#9ca3af] uppercase tracking-wider mb-3">Tungkol sa Pantry</p>
                {pantry.photoUrl && (
                  <div className="rounded-xl overflow-hidden border border-[#d1ead9] h-36 mb-3">
                    <img src={pantry.photoUrl} alt={`Larawan ng ${pantry.pangalan}`} className="w-full h-full object-cover" />
                  </div>
                )}
                {pantry.description && (
                  <p className="text-sm text-[#374151] leading-relaxed">{pantry.description}</p>
                )}
              </div>
            )}
            <div>
              <p className="text-xs font-semibold text-[#9ca3af] uppercase tracking-wider mb-2">Makipag-ugnayan</p>
              {pantry.contactPerson && (
                <div className="flex items-center gap-2 text-sm text-[#374151] mb-2">
                  <User size={15} className="text-[#2d7a4f] flex-shrink-0" />
                  <span className="font-medium">{pantry.contactPerson}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Phone size={15} className="text-[#2d7a4f] flex-shrink-0" />
                <span className="text-sm text-[#374151] flex-1">{pantry.contact}</span>
                <button
                  onClick={handleCopyPhone}
                  title="Kopyahin ang numero"
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border transition-all ${
                    copiedPhone
                      ? 'bg-[#dcfce7] text-[#16a34a] border-[#bbf7d0]'
                      : 'bg-white text-[#6b7280] border-[#e5e7eb] hover:border-[#d1ead9] hover:text-[#374151]'
                  }`}
                >
                  <Copy size={12} />
                  {copiedPhone ? 'Nakopya!' : 'Kopyahin'}
                </button>
                <a
                  href={`tel:${pantry.contact.replace(/\s/g, '')}`}
                  title="Tumawag"
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border bg-[#e8f5ee] text-[#2d7a4f] border-[#d1ead9] hover:bg-[#d1ead9] transition-all"
                >
                  <PhoneCall size={12} />
                  Tawagan
                </a>
              </div>
              {hasSocial && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {pantry.facebookUrl && (
                    <a href={buildSocialUrl(pantry.facebookUrl, 'facebook')} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#1877f2]/10 text-[#1877f2] hover:bg-[#1877f2]/20 transition-colors border border-[#1877f2]/20">
                      <FacebookIcon size={13} /> Facebook
                    </a>
                  )}
                  {pantry.instagramUrl && (
                    <a href={buildSocialUrl(pantry.instagramUrl, 'instagram')} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#e1306c]/10 text-[#e1306c] hover:bg-[#e1306c]/20 transition-colors border border-[#e1306c]/20">
                      <InstagramIcon size={13} /> Instagram
                    </a>
                  )}
                  {pantry.twitterUrl && (
                    <a href={buildSocialUrl(pantry.twitterUrl, 'twitter')} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-black/10 text-black hover:bg-black/20 transition-colors border border-black/10">
                      <XTwitterIcon size={13} /> X / Twitter
                    </a>
                  )}
                  {pantry.messengerUrl && (
                    <a href={buildSocialUrl(pantry.messengerUrl, 'messenger')} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#0084ff]/10 text-[#0084ff] hover:bg-[#0084ff]/20 transition-colors border border-[#0084ff]/20">
                      <MessageCircle size={13} /> Messenger
                    </a>
                  )}
                </div>
              )}
            </div>
            <div>
              <p className="text-xs font-semibold text-[#9ca3af] uppercase tracking-wider mb-2">Organizer</p>
              <div className="flex items-center gap-2 text-sm text-[#374151]">
                <User size={15} className="text-[#2d7a4f] flex-shrink-0" />
                <span>{pantry.nagtatag}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-[#9ca3af] pt-1 border-t border-[#e8f5ee]">
              <Calendar size={13} />
              <span>Huling na-update: {formatDate(pantry.hulingUpdate)}</span>
            </div>
            {nearbyPantries.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-[#9ca3af] uppercase tracking-wider mb-2">Pantry na Malapit</p>
                <div className="space-y-2">
                  {nearbyPantries.map((np) => {
                    const npConfig = STATUS_CONFIG[np.status];
                    const NpIcon = npConfig.icon;
                    return (
                      <button
                        key={`nearby-${np.id}`}
                        onClick={() => onSelectPantry?.(np)}
                        className="w-full flex items-center gap-3 p-2.5 bg-[#f8fdf9] border border-[#d1ead9] rounded-xl text-left hover:bg-[#edf7f1] hover:border-[#a7d9b8] transition-colors cursor-pointer"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-[#1e5c38] truncate">{np.pangalan}</p>
                          <p className="text-xs text-[#6b7280] truncate">{np.barangay}, {np.lungsod}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-semibold ${npConfig.className}`}>
                            <NpIcon size={9} className={npConfig.iconColor} />
                            {npConfig.label}
                          </span>
                          <span className="text-xs font-semibold text-[#2d7a4f] bg-[#e8f5ee] px-2 py-0.5 rounded-full">
                            {np.distance < 1 ? `${Math.round(np.distance * 1000)}m` : `${np.distance.toFixed(1)}km`}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Desktop Action Buttons */}
          <div className="px-5 py-4 border-t border-[#e8f5ee] space-y-2 bg-white flex-shrink-0">
            <a
              href={profileUrl}
              className="flex items-center justify-center gap-2 w-full bg-[#f0faf4] border border-[#d1ead9] text-[#2d7a4f] hover:bg-[#e8f5ee] active:scale-95 font-semibold text-sm py-2.5 rounded-xl transition-all duration-150"
            >
              <BookOpen size={15} />
              Profile ng Pantry
            </a>
            <a
              href={directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full bg-[#2d7a4f] hover:bg-[#1e5c38] active:scale-95 text-white font-semibold text-sm py-3 rounded-xl transition-all duration-150"
            >
              <ExternalLink size={16} />
              Puntahan
            </a>
            {onDonate && (
              <button
                onClick={onDonate}
                className="flex items-center justify-center gap-2 w-full bg-[#fff7ed] border border-[#fed7aa] text-[#c2410c] hover:bg-[#ffedd5] active:scale-95 font-semibold text-sm py-2.5 rounded-xl transition-all duration-150"
              >
                <Heart size={15} />
                Mag-donate sa Pantry na Ito
              </button>
            )}
            <button
              onClick={() => onReport(pantry)}
              className="flex items-center justify-center gap-2 w-full border border-[#d1ead9] text-[#6b7280] hover:bg-[#f9fafb] active:scale-95 font-medium text-sm py-2.5 rounded-xl transition-all duration-150"
            >
              <MessageCircle size={15} />
              Magbigay ng Feedback
            </button>
            <Link
              href={manageUrl}
              className="flex items-center justify-center gap-2 w-full border border-[#d1ead9] text-[#2d7a4f] hover:bg-[#f0faf4] active:scale-95 font-medium text-sm py-2.5 rounded-xl transition-all duration-150"
            >
              <RefreshCw size={15} />
              Manage
            </Link>
          </div>
        </>
      )}
    </div>
  );
}